// src/lib/socketManager.ts
import { writable, derived, get } from 'svelte/store';
import io, { Socket } from 'socket.io-client';
import { storage } from './storage';
import { apiBaseUrl } from './constants';
import { onlineUsers } from '../stores/onlineUsersStore';
import { refreshToken } from './fetchUser';
import { captureError } from './errorTracker';
import { getSharedAccountSocketQuery } from './sharedAccount';

export type SocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error' | 'reconnecting';

interface SocketState {
  socket: Socket | null;
  status: SocketStatus;
  error: string | null;
  reconnectAttempts: number;
}

interface EventSubscription {
  event: string;
  handler: (...args: any[]) => void;
  component: string; // For debugging
}

class SocketManager {
  private state = writable<SocketState>({
    socket: null,
    status: 'disconnected',
    error: null,
    reconnectAttempts: 0,
  });

  private eventSubscriptions = new Map<string, Set<EventSubscription>>();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Fast reconnection every 1 second
  private tokenRefreshPromise: Promise<void> | null = null;
  private authErrorCount = 0;
  private maxAuthErrors = 3;
  private lastAuthError = 0;
  private connectionLock = false;
  private authErrorHandling = false;

  // Derived stores for easy access
  public socket = derived(this.state, $state => $state.socket);
  public status = derived(this.state, $state => $state.status);
  public isConnected = derived(this.state, $state => $state.status === 'connected');
  public error = derived(this.state, $state => $state.error);

  async connect(): Promise<void> {
    const currentState = get(this.state);
    
    // Prevent multiple connection attempts with lock
    if (this.connectionLock || currentState.status === 'connecting' || currentState.status === 'connected') {
      console.log('Connection attempt blocked - already connecting/connected or locked');
      return;
    }

    // Set connection lock
    this.connectionLock = true;

    try {
      // Check if user is authenticated before connecting
      let token = await storage.get<string>('authJwt');
      if (!token) {
        console.log('No auth token found, skipping socket connection');
        this.updateStatus('error', 'Not authenticated');
        this.connectionLock = false;
        return;
      }

      // Reset auth errors on manual connect
      this.authErrorCount = 0;

      this.updateStatus('connecting');
      
      // Get fresh token
    token = await this.getValidToken();
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      const sharedSocketQuery = await getSharedAccountSocketQuery();

      const socket = io(apiBaseUrl, {
        query: {
          authToken: token,
          ...sharedSocketQuery,
        },
        transports: ['websocket'],
        reconnection: false, // We handle reconnection ourselves
        timeout: 20000,
        forceNew: true, // Force a new connection to clear any stale connections
      });

      this.setupSocketListeners(socket);
      
      this.state.update(state => ({
        ...state,
        socket,
        error: null,
      }));

    } catch (error) {
      captureError(error as Error, { context: 'socket_connect' });
      this.updateStatus('error', (error as Error).message);
      this.scheduleReconnect();
    } finally {
      // Always release the connection lock
      this.connectionLock = false;
    }
  }

  private setupSocketListeners(socket: Socket): void {
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      this.updateStatus('connected');
      this.state.update(state => ({ ...state, reconnectAttempts: 0 }));
      
      // Reset auth error count on successful connection
      this.authErrorCount = 0;
      
      // Release connection lock on successful connection
      this.connectionLock = false;
      
      // Re-subscribe to all events
      this.resubscribeAllEvents();
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.updateStatus('disconnected');
      
      // Handle different disconnect reasons
      if (reason === 'io server disconnect') {
        // Server forcefully disconnected, might be auth issue
        this.handleAuthError();
      } else {
        // Client-side disconnect or network issue
        this.scheduleReconnect();
      }
    });

    socket.on('connect_error', (error) => {
      captureError(new Error(`Socket connection error: ${error.message}`), {
        context: 'socket_connect_error',
        errorType: (error as any).type,
      });
      
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        this.handleAuthError();
      } else {
        this.updateStatus('error', error.message);
        this.scheduleReconnect();
      }
    });

    socket.on('error', (error) => {
      captureError(new Error(`Socket error: ${error}`), { context: 'socket_error' });
      this.updateStatus('error', error.toString());
    });

    // Handle online users updates
    socket.on('onlineUsers', (users) => {
      onlineUsers.set(users);
    });

    // Heartbeat to detect stale connections
    socket.on('ping', () => {
      socket.emit('pong');
    });
  }

  private async getValidToken(): Promise<string | null> {
    try {
      // Check if we have a token
      let token = await storage.get<string>('authJwt');
      
      if (!token) {
        return null;
      }

      // Try to refresh the token to ensure it's valid
      // This prevents issues with expired tokens
      if (!this.tokenRefreshPromise) {
        this.tokenRefreshPromise = refreshToken()
          .then((success) => {
            if (!success) {
              throw new Error('Token refresh failed');
            }
          })
          .finally(() => {
            // ✅ RACE CONDITION FIX: Always clean up promise reference
            this.tokenRefreshPromise = null;
          });
      }
      
      await this.tokenRefreshPromise;
      
      // Get the potentially new token
      token = await storage.get<string>('authJwt');
      return token;
    } catch (error) {
      captureError(error as Error, { context: 'socket_token_refresh' });
      return null;
    }
  }

  private async handleAuthError(): Promise<void> {
    const now = Date.now();
    
    // ✅ RACE CONDITION FIX: Prevent multiple simultaneous auth error handling
    if (this.authErrorHandling) {
      console.log('Auth error handling already in progress, skipping');
      return;
    }
    
    // Prevent auth error spam (min 5 seconds between attempts)
    if (now - this.lastAuthError < 5000) {
      console.log('Auth error too recent, skipping');
      return;
    }
    
    // Set auth error handling flag
    this.authErrorHandling = true;
    
    try {
      this.lastAuthError = now;
      this.authErrorCount++;
      
      console.log(`Socket auth error #${this.authErrorCount}`);
      
      if (this.authErrorCount > this.maxAuthErrors) {
        this.updateStatus('error', 'Too many authentication failures');
        captureError(new Error('Too many socket auth failures - user needs to re-login'), { 
          context: 'socket_max_auth_errors',
          authErrorCount: this.authErrorCount
        });
        this.authErrorHandling = false;
        return;
      }
      
      this.updateStatus('error', 'Authentication failed');
      
      console.log('Attempting to refresh token for socket...');
      
      // Wait before trying to refresh token (fast retry)
      const delay = 1000; // 1 second for fast reconnection
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Try to refresh the token
      const refreshSuccess = await refreshToken();
      if (!refreshSuccess) {
        console.error('Socket auth error: Token refresh failed');
        // Don't throw - just return to avoid breaking the socket flow
        this.authErrorHandling = false;
        return;
      }
      console.log('Token refresh successful');
      
      // Wait briefly before reconnecting
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Reset auth error count on successful refresh
      this.authErrorCount = 0;
      
      // Reconnect with new token
      console.log('Attempting socket reconnection with new token...');
      this.connect();
      
    } catch (error) {
      captureError(error as Error, { 
        context: 'socket_auth_error',
        authErrorCount: this.authErrorCount 
      });
      console.error('Socket token refresh failed:', error);
      
      // If token refresh fails, don't try to reconnect immediately
      // The user needs to handle re-authentication
      if (this.authErrorCount >= this.maxAuthErrors) {
        console.error('Max auth errors reached, stopping reconnection attempts');
      }
    } finally {
      // ✅ RACE CONDITION FIX: Always clear the auth error handling flag
      this.authErrorHandling = false;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    // Always use 1 second delay for fast reconnection
    const delay = 1000;

    this.updateStatus('reconnecting');
    
    this.reconnectTimer = setTimeout(() => {
      this.state.update(state => ({
        ...state,
        reconnectAttempts: state.reconnectAttempts + 1,
      }));
      this.connect();
    }, delay);
  }

  private updateStatus(status: SocketStatus, error: string | null = null): void {
    this.state.update(state => ({
      ...state,
      status,
      error,
    }));
  }

  disconnect(): void {
    // Release connection lock and auth error handling flag
    this.connectionLock = false;
    this.authErrorHandling = false;
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const currentState = get(this.state);
    
    if (currentState.socket) {
      // ✅ MEMORY LEAK FIX: Remove tracked listeners first to prevent memory leaks
      this.eventSubscriptions.forEach((subscriptions, event) => {
        subscriptions.forEach(({ handler }) => {
          currentState.socket!.off(event, handler);
        });
      });
      
      // Then remove any remaining listeners
      currentState.socket.removeAllListeners();
      currentState.socket.disconnect();
    }

    // Clear all tracked subscriptions
    this.eventSubscriptions.clear();
    
    // Reset auth errors on manual disconnect
    this.authErrorCount = 0;
    
    this.state.set({
      socket: null,
      status: 'disconnected',
      error: null,
      reconnectAttempts: 0,
    });
  }

  reconnect(): void {
    this.disconnect();
    this.connect();
  }

  // Force clear any stale connections and reset everything
  forceReset(): void {
    console.log('Force resetting socket manager...');
    
    // Release connection lock and auth error handling flag first
    this.connectionLock = false;
    this.authErrorHandling = false;
    
    // Clear all timers and state
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Reset all error counts
    this.authErrorCount = 0;
    this.lastAuthError = 0;
    
    // Clear token refresh promise
    this.tokenRefreshPromise = null;
    
    // Disconnect and clear everything
    this.disconnect();
    
    // Wait a moment then try fresh connection
    setTimeout(() => {
      console.log('Attempting fresh connection after reset...');
      this.connect();
    }, 2000);
  }

  // Event subscription management
  on(event: string, handler: (...args: any[]) => void, component: string = 'unknown'): () => void {
    const currentState = get(this.state);
    
    if (currentState.socket && currentState.status === 'connected') {
      currentState.socket.on(event, handler);
    }

    // Store the subscription for reconnection
    if (!this.eventSubscriptions.has(event)) {
      this.eventSubscriptions.set(event, new Set());
    }
    
    const subscription: EventSubscription = { event, handler, component };
    this.eventSubscriptions.get(event)!.add(subscription);

    // Return cleanup function
    return () => {
      const subs = this.eventSubscriptions.get(event);
      if (subs) {
        subs.delete(subscription);
        if (subs.size === 0) {
          this.eventSubscriptions.delete(event);
        }
      }

      const state = get(this.state);
      if (state.socket) {
        state.socket.off(event, handler);
      }
    };
  }

  async emit(event: string, data?: any): Promise<void> {
    const currentState = get(this.state);
    
    if (currentState.socket && currentState.status === 'connected') {
      currentState.socket.emit(event, data);
    } else {
      // Check if we have auth token before warning
      const token = await storage.get<string>('authJwt');
      if (!token) {
        console.log(`Cannot emit '${event}': user not authenticated`);
      } else {
        console.warn(`Cannot emit '${event}': socket not connected`);
      }
    }
  }

  // Re-subscribe all events after reconnection
  private resubscribeAllEvents(): void {
    const currentState = get(this.state);
    
    if (!currentState.socket || currentState.status !== 'connected') {
      return;
    }

    this.eventSubscriptions.forEach((subscriptions, event) => {
      subscriptions.forEach(({ handler }) => {
        currentState.socket!.on(event, handler);
      });
    });
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const token = await storage.get<string>('authJwt');
    return !!token;
  }

  // Utility method to wait for connection
  async waitForConnection(timeout: number = 5000): Promise<boolean> {
    const startTime = Date.now();
    
    while (Date.now() - startTime < timeout) {
      const state = get(this.state);
      if (state.status === 'connected') {
        return true;
      }
      
      if (state.status === 'error') {
        return false;
      }
      
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    return false;
  }

  // Debug method
  getDebugInfo(): any {
    const state = get(this.state);
    const subscriptions: Record<string, string[]> = {};
    
    this.eventSubscriptions.forEach((subs, event) => {
      subscriptions[event] = Array.from(subs).map(s => s.component);
    });
    
    return {
      status: state.status,
      error: state.error,
      reconnectAttempts: state.reconnectAttempts,
      authErrorCount: this.authErrorCount,
      connectionLock: this.connectionLock,
      authErrorHandling: this.authErrorHandling,
      socketId: state.socket?.id,
      connected: state.socket?.connected,
      eventSubscriptions: subscriptions,
    };
  }
}

// Create singleton instance
export const socketManager = new SocketManager();

// Export convenience functions
export const { socket, status, isConnected, error } = socketManager;

// Debug: expose socket manager globally for debugging
if (typeof window !== 'undefined') {
  (window as any).socketManager = socketManager;
  console.log('Socket manager available globally as window.socketManager');
}
